import { useStickToBottomContext } from 'use-stick-to-bottom';
import React, { useEffect } from 'react';
import { useSignalEffect } from '@preact/signals-react';
import { IoArrowDown, IoArrowUp } from 'react-icons/io5';
import styled from 'styled-components';
import { useChatSession } from '@/features/ai-agents/chat/context';

export interface ScrollToButtonsProps {
  onChange?: (isAtBottom: boolean, isAtTop: boolean) => void;
}

export function ScrollToButtons({ onChange }: ScrollToButtonsProps) {
  const { isAtBottom, scrollToBottom, scrollRef } = useStickToBottomContext();
  const { isProcessing } = useChatSession();

  const [isAtTop, setIsAtTop] = React.useState(false);

  useEffect(() => {
    if (!scrollRef.current) return;

    const handleScroll = () => {
      if (!scrollRef.current) return;
      setIsAtTop(scrollRef.current.scrollTop === 0);
    };
    scrollRef.current.addEventListener(`scroll`, handleScroll);

    // eslint-disable-next-line consistent-return
    return () => {
      if (!scrollRef.current) return;
      scrollRef.current.removeEventListener(`scroll`, handleScroll);
    };
  }, [scrollRef]);

  useSignalEffect(() => {
    if (isProcessing.value) {
      scrollToBottom();
    }
  });

  useEffect(() => {
    onChange?.(isAtBottom, isAtTop);
  }, [isAtTop, isAtBottom]);

  const scrollToPrevUserMessage = () => {
    if (!scrollRef.current) return;

    const container = scrollRef.current;
    const userMessages = Array.from(
      container.querySelectorAll(`.ai-user-message`),
    ) as HTMLElement[];

    const containerRect = container.getBoundingClientRect();
    const viewportTop = containerRect.top;

    const targetMessage = userMessages.reverse().find((msg) => {
      const msgRect = msg.getBoundingClientRect();
      const msgTop = msgRect.top;

      return msgTop < viewportTop - 20;
    });

    if (targetMessage) {
      const targetPosition = targetMessage.offsetTop - 10;
      container.scrollTo({
        top: targetPosition,
        behavior: `smooth`,
      });
    }
  };

  const scrollToNextUserMessageOrBottom = () => {
    if (!scrollRef.current) return;

    const container = scrollRef.current;
    const userMessages = Array.from(
      container.querySelectorAll(`.ai-user-message`),
    ) as HTMLElement[];

    const containerRect = container.getBoundingClientRect();
    const viewportTop = containerRect.top;

    const targetMessage = userMessages.find((msg) => {
      const msgRect = msg.getBoundingClientRect();
      const msgTop = msgRect.top;

      return msgTop > viewportTop + 20;
    });

    if (targetMessage) {
      const targetPosition = targetMessage.offsetTop - 10;
      container.scrollTo({
        top: targetPosition,
        behavior: `smooth`,
      });
    } else {
      scrollToBottom();
    }
  };

  return (
    <ScrollToWrapper>
      <ScrollToBottomButton
        onClick={scrollToNextUserMessageOrBottom}
        visible={!isAtBottom}
        aria-label="Scroll to bottom"
      >
        <IoArrowDown size={20} />
      </ScrollToBottomButton>

      {!isAtTop && (
        <ScrollToBottomButton
          onClick={scrollToPrevUserMessage}
          visible={!isAtBottom}
          aria-label="Scroll to bottom"
        >
          <IoArrowUp size={20} />
        </ScrollToBottomButton>
      )}
    </ScrollToWrapper>
  );
}

const ScrollToWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  gap: 10px;
  z-index: 10;
  padding-bottom: 1rem;
`;

const ScrollToBottomButton = styled.button<{ visible: boolean }>`
  outline: none;
  border: none;
  appearance: none;

  background-color: white;
  box-shadow: 0 2px 10px rgb(0 0 0 / 0.1), 0 8px 10px rgb(0 0 0 / 0.06);
  border-radius: 100%;

  height: 3.5rem;
  width: 3.5rem;

  align-items: center;
  justify-content: center;

  cursor: pointer;

  transition: opacity 0.3s, transform 0.3s;
  opacity: ${(p) => (p.visible ? 1 : 0)};
  display: ${(p) => (p.visible ? `flex` : `none`)};
  pointer-events: ${(p) => (p.visible ? `auto` : `none`)};
  transform: translateY(${(p) => (p.visible ? `0` : `40px`)});
`;
